// @ts-check
/**
 * Video thumbnail helper.
 *
 * Videos now live on Cloudflare R2 as plain files, which has no URL-transform API —
 * so for R2 videos there is no still-frame URL and this returns null. Callers render
 * a muted `<video preload="metadata">` in that case, which shows the first frame.
 *
 * Rows uploaded before the migration still point at res.cloudinary.com; for those the
 * old `f_jpg,so_0` transform keeps working until the backfill moves them.
 */

const CLOUDINARY_HOST = 'res.cloudinary.com';

/**
 * Whether a URL points at a still-Cloudinary-hosted asset.
 * @param {string} url
 */
export function isCloudinaryUrl(url) {
  try {
    return new URL(url).hostname === CLOUDINARY_HOST;
  } catch {
    return false;
  }
}

/**
 * Builds a still-frame thumbnail URL for a video, or null when the host can't produce one.
 *
 * @param {string} mediaUrl - Video URL.
 * @param {{ width?: number }} [options]
 * @returns {string | null} The thumbnail URL, or null for R2-hosted video.
 */
export function getVideoThumbnailUrl(mediaUrl, { width } = {}) {
  if (!mediaUrl || !isCloudinaryUrl(mediaUrl)) return null;
  const transform = width ? `f_jpg,so_0,w_${width}` : 'f_jpg,so_0';
  return mediaUrl.replace(/\/upload\//, `/upload/${transform}/`);
}

const VIDEO_EXTENSIONS = /\.(mp4|mov|avi|webm)(\?|#|$)/i;

/**
 * Best-effort "is this URL a video" check for records with no stored media type —
 * e.g. scheduled posts and fetched target-account posts, which only ever store a bare
 * media_url. Prefer a real type field (KaayPost.post_type, upload response resource_type)
 * over this whenever one is available; this is a fallback for when there isn't one.
 * @param {string} [url]
 */
export function looksLikeVideoUrl(url) {
  return Boolean(url && VIDEO_EXTENSIONS.test(url));
}
